import { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { MultiPhotoUploader } from '../components/MultiPhotoUploader';
import { LocationAutocomplete } from '../components/LocationAutocomplete';
import { useAuth } from '../context/AuthContext';
import { useLocation as useUserLocation } from '../context/LocationContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '../components/ui/select';
import { toast } from '../lib/toast';
import {
  Car,
  ChevronLeft,
  MapPin,
  Camera,
  Loader2,
} from 'lucide-react';

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: CURRENT_YEAR + 1 - 1950 + 1 }, (_, i) => CURRENT_YEAR + 1 - i);

export default function AddFreeVehicleListingPage() {
  const { user, api } = useAuth();
  const { location: userLocation } = useUserLocation();
  const navigate = useNavigate();

  const [submitting, setSubmitting] = useState(false);
  const [makesCatalog, setMakesCatalog] = useState([]);

  const [form, setForm] = useState({
    title: '',
    price: '',
    is_eoi: false,
    description: '',
    images: [],
    primary_image_index: 0,
    make: '',
    make_other: '',
    model: '',
    year: '',
    km: '',
    location_name: userLocation?.name || user?.location_name || '',
    location: userLocation
      ? { lat: userLocation.lat, lng: userLocation.lng }
      : user?.location
        ? { lat: user.location.lat, lng: user.location.lng }
        : null,
  });

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/inventory/vehicle-makes');
        if (!cancelled) setMakesCatalog((res.data || {}).cars || []);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!form.make && !form.model) return;
    const parts = [form.year, form.make === '__other__' ? form.make_other : form.make, form.model].filter(Boolean);
    const suggested = parts.join(' ');
    if (suggested && !form.title) {
      set('title', suggested);
    }
  }, [form.make, form.make_other, form.model, form.year]); // eslint-disable-line react-hooks/exhaustive-deps

  const resolvedMake = form.make === '__other__' ? form.make_other : form.make;

  const canSubmit = useMemo(() => {
    return !!(
      form.title.trim() &&
      (form.is_eoi || (form.price !== '' && parseFloat(form.price) >= 0)) &&
      form.description.trim().length >= 20 &&
      form.images.length >= 1 &&
      form.location
    );
  }, [form.title, form.is_eoi, form.price, form.description, form.images.length, form.location]);

  async function handleSubmit() {
    if (!form.title.trim()) { toast.error('Please enter a title'); return; }
    if (!form.is_eoi && (form.price === '' || form.price === null)) { toast.error('Please enter a price or select EOI'); return; }
    if (form.description.trim().length < 20) { toast.error('Description must be at least 20 characters'); return; }
    if (form.images.length < 1) { toast.error('Please add at least 1 photo'); return; }
    if (!form.location) { toast.error('Please set a location'); return; }

    setSubmitting(true);
    try {
      const price = form.is_eoi ? 0 : (parseFloat(form.price) || 0);
      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        category: 'vehicles',
        subcategory: 'cars',
        estimated_value: price,
        price,
        condition: 'good',
        is_free: false,
        is_eoi: form.is_eoi,
        is_firm: false,
        is_public: true,
        location: form.location,
        location_name: form.location_name,
        images: form.images,
        primary_image_index: form.primary_image_index,
        tags: [],
        vehicle_details: {
          subcategory: 'cars',
          make: resolvedMake || null,
          model: form.model || null,
          year: form.year ? parseInt(form.year) : null,
          km: form.km ? parseInt(form.km) : null,
          listing_tier: 'basic',
        },
      };

      const res = await api.post('/inventory/', payload);
      toast.success('Vehicle listed successfully!');
      navigate(`/auto/cars/${res.data.id}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 429) {
        toast.error(detail || 'You already have an active free listing. Remove it first or upgrade to Plus.');
      } else {
        toast.error(detail || 'Failed to submit listing. Please check all required fields.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Link to="/auto/cars/list" className="text-[var(--theme-text-muted)] hover:text-[var(--theme-text-primary)]">
            <ChevronLeft className="w-5 h-5" />
          </Link>
          <div>
            <div className="flex items-center gap-2">
              <Car className="w-5 h-5 text-[#1E3A5F]" />
              <h1 className="text-xl font-bold text-[var(--theme-text-primary)]">Free Listing</h1>
            </div>
            <p className="text-sm text-[var(--theme-text-muted)]">
              Quick &amp; easy &middot; 1 active at a time &middot; 30 days
            </p>
          </div>
        </div>

        <div className="bg-[var(--theme-card)] rounded-xl border border-[var(--theme-border)] p-6">
          <div className="space-y-5">

            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-[var(--theme-text-secondary)] mb-1">
                Title <span className="text-red-500">*</span>
              </label>
              <Input
                value={form.title}
                onChange={(e) => set('title', e.target.value)}
                placeholder="e.g. 2019 Toyota Hilux SR5"
                maxLength={80}
              />
              <p className="text-xs text-[var(--theme-text-muted)] mt-1">{form.title.length}/80</p>
            </div>

            {/* EOI + Price */}
            <div>
              <div className="flex items-center gap-3 mb-2">
                <input
                  type="checkbox"
                  checked={form.is_eoi}
                  onChange={(e) => { set('is_eoi', e.target.checked); if (e.target.checked) set('price', ''); }}
                  className="w-4 h-4 accent-[#1E3A5F]"
                  id="eoi-check"
                />
                <label htmlFor="eoi-check" className="text-sm text-[var(--theme-text-secondary)]">
                  Expression of Interest (EOI) &mdash; price on request
                </label>
              </div>
              {!form.is_eoi && (
                <div>
                  <label className="block text-sm font-medium text-[var(--theme-text-secondary)] mb-1">
                    Asking Price (AUD) <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--theme-text-muted)]">$</span>
                    <Input
                      type="number"
                      value={form.price}
                      onChange={(e) => set('price', e.target.value)}
                      placeholder="0"
                      min="0"
                      className="pl-7"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-[var(--theme-text-secondary)] mb-1">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                value={form.description}
                onChange={(e) => set('description', e.target.value)}
                placeholder="Tell buyers about this vehicle — condition, features, why you're selling..."
                maxLength={500}
                rows={4}
                className="w-full rounded-lg border border-[var(--theme-border)] bg-[var(--theme-card)] px-3 py-2 text-sm text-[var(--theme-text-primary)] placeholder:text-[var(--theme-text-muted)] focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/30 resize-none"
              />
              <p className={`text-xs mt-1 ${form.description.length < 20 ? 'text-amber-500' : 'text-[var(--theme-text-muted)]'}`}>
                {form.description.length}/500 {form.description.length < 20 && form.description.length > 0 ? `(${20 - form.description.length} more needed)` : ''}
              </p>
            </div>

            {/* Photos */}
            <div>
              <label className="block text-sm font-medium text-[var(--theme-text-secondary)] mb-1">
                <Camera className="w-4 h-4 inline mr-1" />
                Photos <span className="text-red-500">*</span>
                <span className="text-xs text-[var(--theme-text-muted)] ml-2">1–5 photos</span>
              </label>
              <MultiPhotoUploader
                images={form.images}
                onImagesChange={(imgs) => set('images', imgs)}
                maxImages={5}
                primaryIndex={form.primary_image_index}
                onPrimaryChange={(i) => set('primary_image_index', i)}
                api={api}
              />
            </div>

            {/* Vehicle Details (optional) */}
            <div className="border-t border-[var(--theme-border)] pt-5">
              <p className="text-sm font-medium text-[var(--theme-text-secondary)] mb-3">
                Vehicle Details <span className="text-xs text-[var(--theme-text-muted)]">(optional — helps buyers find your listing)</span>
              </p>

              <div className="grid grid-cols-2 gap-3">
                {/* Make */}
                <div>
                  <label className="block text-xs text-[var(--theme-text-muted)] mb-1">Make</label>
                  <Select value={form.make} onValueChange={(v) => set('make', v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select make" />
                    </SelectTrigger>
                    <SelectContent>
                      {makesCatalog.map((m) => (
                        <SelectItem key={m} value={m}>{m}</SelectItem>
                      ))}
                      <SelectItem value="__other__">Other...</SelectItem>
                    </SelectContent>
                  </Select>
                  {form.make === '__other__' && (
                    <Input
                      className="mt-1"
                      value={form.make_other}
                      onChange={(e) => set('make_other', e.target.value)}
                      placeholder="Enter make"
                    />
                  )}
                </div>

                {/* Model */}
                <div>
                  <label className="block text-xs text-[var(--theme-text-muted)] mb-1">Model</label>
                  <Input
                    value={form.model}
                    onChange={(e) => set('model', e.target.value)}
                    placeholder="e.g. Hilux"
                  />
                </div>

                {/* Year */}
                <div>
                  <label className="block text-xs text-[var(--theme-text-muted)] mb-1">Year</label>
                  <Select value={form.year} onValueChange={(v) => set('year', v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Year" />
                    </SelectTrigger>
                    <SelectContent>
                      {YEAR_OPTIONS.map((y) => (
                        <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Odometer */}
                <div>
                  <label className="block text-xs text-[var(--theme-text-muted)] mb-1">Odometer (km)</label>
                  <Input
                    type="number"
                    value={form.km}
                    onChange={(e) => set('km', e.target.value)}
                    placeholder="e.g. 85000"
                    min="0"
                  />
                </div>
              </div>
            </div>

            {/* Location */}
            <div>
              <label className="block text-sm font-medium text-[var(--theme-text-secondary)] mb-1">
                <MapPin className="w-4 h-4 inline mr-1" />
                Location <span className="text-red-500">*</span>
              </label>
              <LocationAutocomplete
                value={form.location_name}
                onChange={(val) => set('location_name', val)}
                onSelect={(loc) => {
                  set('location_name', loc.name);
                  set('location', { lat: loc.lat, lng: loc.lng });
                }}
                placeholder="Enter suburb or postcode..."
              />
              {form.location_name && (
                <p className="text-xs text-[var(--theme-text-muted)] mt-1">
                  {form.location ? '\u2713 Location set' : 'Select a suggestion to confirm location'}
                </p>
              )}
            </div>

            {/* Submit */}
            <Button
              onClick={handleSubmit}
              disabled={!canSubmit || submitting}
              className="w-full bg-[#1E3A5F] hover:bg-[#152d4a] text-white rounded-full py-3 text-base font-semibold"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Publishing...</>
              ) : (
                'Publish Free Listing'
              )}
            </Button>

          </div>
        </div>
      </div>
    </Layout>
  );
}
